// ─── Fiche d'un article — LECTURE SEULE (règle app-wide MoPCal) ───
// Clic sur un article = cette fiche (jamais l'édition directe). ✏️ et 🗑️ en
// haut à droite. Reproduit la fiche MoPCal : photo, élément, emplacement + mini
// plan 2D, historique d'inventaire, achat (prix + date), code-barres, photos.
import React, { useMemo, useState } from 'react';
import { View, Text, Image, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useUI, useT, useLangue, Feuille, VisionneuseImage } from '@mop/core';
import {
  articlesDe, photosArticle, cheminDe, noeudParId, parentDe,
} from './services/inventaires';
import { cheminIdsEmp } from './services/plan';
import PlanGrid from './ui/PlanGrid';
import LocalisationModal from './LocalisationModal';

const dateLoc = (v, locale = 'fr-FR') => {
  if (!v) return null;
  // Accepte ISO (comptage) ou une saisie libre (date d'achat) — on n'impose rien.
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    try { return new Date(v).toLocaleDateString(locale); } catch { return v; }
  }
  return v;
};
const prixFr = (p) => (p == null || p === '' ? null : `${Number(p).toFixed(2).replace('.', ',')} €`);

export default function FicheElement({
  visible, item, articleIndex = 0, emplacements = [], arbre = [], compteDe,
  onFermer, onEditer, onSupprimer,
}) {
  const UI = useUI();
  const t = useT();
  const { langue } = useLangue();
  const dateFr = (v) => dateLoc(v, langue === 'en' ? 'en-GB' : 'fr-FR');
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [localiser, setLocaliser] = useState(false);   // calque « Où est-ce rangé ? »
  const [zoom, setZoom] = useState(null);              // image ouverte en plein écran (tap)

  // TOUS les hooks AVANT le moindre return conditionnel (règle des hooks) :
  // sinon, à l'ouverture de la fiche, React voit un hook de plus qu'au rendu
  // précédent (feuille fermée) et lève « more hooks than previous render » →
  // écran blanc. On rend donc les dérivés null-safe.
  const arts = item ? articlesDe(item) : [];
  const a = arts[articleIndex] || arts[0] || {};

  // Mini-plan : le plan du PARENT de l'emplacement, la pastille surlignée.
  const parentPlan = useMemo(() => {
    if (!a.emplacementId) return null;
    const p = parentDe(emplacements, a.emplacementId);
    return p && p.parent ? p.parent : null;    // null si l'emplacement est à la racine
  }, [emplacements, a.emplacementId]);

  if (!visible || !item) return null;

  const photos = photosArticle(a);
  const chemin = a.emplacementId ? cheminDe(emplacements, a.emplacementId) : '';
  const chemCat = item.catId ? cheminDe(arbre, item.catId) : '';
  const qte = Number(a.qte) || 0;
  const present = a.unique ? qte > 0 : true;

  // Historique d'inventaire — MoPCal le stocke dans `a.histInv`
  // [{ date, etat, deplace, empAvant, empApres, raison }]. On lit ce champ en
  // priorité (compat MoPCal), avec repli sur nos anciens formats. Trié récent→ancien.
  const histInv = (Array.isArray(a.histInv) ? a.histInv
    : Array.isArray(a.historique) ? a.historique
    : (a.dernierPointage ? [a.dernierPointage] : []))
    .map(h => ({
      date:     h.date || h.le || '',
      etat:     h.etat || 'present',
      deplace:  !!h.deplace,
      empAvant: h.empAvant, empApres: h.empApres,
      raison:   h.raison || h.motif || '',
    }))
    .sort((x, y) => (y.date || '').localeCompare(x.date || ''));
  const cheminCourt = (id) => noeudParId(emplacements, id)?.nom || '—';
  const etatLabel = (e) => (e === 'present' ? `✅ ${t('inventaire.present')}` : e === 'absent' ? `❌ ${t('inventaire.absent')}` : `❔ ${t('inventaire.indetermine')}`);
  // Chemin d'emplacement en ARBRE (comme MoPCal cheminArbre) : un niveau par
  // ligne, 📍 sur le premier puis ↳ indentés.
  const cheminParts = chemin ? chemin.split(' › ').filter(Boolean) : [];
  const w50 = Math.round(Dimensions.get('window').width * 0.5) - 28;   // plan = ~50 % largeur

  return (
    <Feuille visible titre={a.nom || item.nom} emoji="🏷️" onFermer={onFermer} sansScroll
      boutons={(
        <>
          {!!onEditer && (
            <TouchableOpacity onPress={onEditer} hitSlop={HIT} accessibilityLabel={t('commun.editer')}><Text style={s.hIc}>✏️</Text></TouchableOpacity>
          )}
          {!!onSupprimer && (
            <TouchableOpacity onPress={onSupprimer} hitSlop={HIT} accessibilityLabel={t('commun.supprimer')}><Text style={[s.hIc, { color: UI.danger }]}>🗑️</Text></TouchableOpacity>
          )}
        </>
      )}>
          <ScrollView contentContainerStyle={s.corps}>
            <View style={s.entete}>
              {photos[0]
                ? <TouchableOpacity activeOpacity={0.85} onPress={() => setZoom(photos[0].url)}><Image source={{ uri: photos[0].url }} style={s.grandePhoto} /></TouchableOpacity>
                : <View style={[s.grandePhoto, s.photoVide]}><Text style={{ fontSize: 30 }}>{item.emoji || '📦'}</Text></View>}
              <View style={{ flex: 1 }}>
                <Text style={s.titre}>{a.nom || item.nom}</Text>
                <Text style={s.sous}>
                  {a.unique
                    ? `${t('inventaire.exemplaireUnique')} · ${present ? t('inventaire.present') : t('inventaire.absent')}`
                    : `${t('inventaire.ficheQuantite', { n: qte })}${Number(a.seuil) > 0 ? ` · ${t('inventaire.seuil')} ${Number(a.seuil)}` : ''}`}
                  {a.consommable ? ` · ${t('inventaire.consommable')}` : ''}
                </Text>
              </View>
            </View>

            {/* Deux colonnes 50/50 (comme MoPCal FicheArticleEntete) :
                à gauche l'élément + le chemin d'emplacement (cliquable), à droite
                le plan 2D réduit. Tout ouvre « Où est-ce rangé ? ». */}
            <View style={s.colsRow}>
              <View style={s.colTree}>
                <Text style={s.ligneFiche}>{item.emoji || '💥'} {item.nom}</Text>
                {!!chemCat && <Text style={s.ligneFaint}>{chemCat}</Text>}
                {!!chemin && (
                  <TouchableOpacity onPress={() => setLocaliser(true)} activeOpacity={0.7} style={{ marginTop: 6 }}>
                    {cheminParts.map((p, i) => (
                      <Text key={i} style={[s.empLigne, { marginLeft: i * 16 }]} numberOfLines={1}>
                        {i === 0 ? '📍 ' : '↳ '}{p}
                      </Text>
                    ))}
                  </TouchableOpacity>
                )}
              </View>
              {!!chemin && parentPlan && (parentPlan.enfants || []).length > 0 && (
                <View style={s.colPlan}>
                  <TouchableOpacity activeOpacity={0.9} onPress={() => setLocaliser(true)}>
                    <PlanGrid parent={parentPlan} flux compact maxWidth={w50}
                      cibleIds={cheminIdsEmp(emplacements, a.emplacementId)} />
                  </TouchableOpacity>
                </View>
              )}
            </View>

            {/* Détails livre (scan ISBN) : auteur, éditeur, parution, pages. */}
            {(a.auteur || a.editeur || a.annee || a.pages) && (
              <>
                <Text style={s.sect}>{t('inventaire.detailsTitre')}</Text>
                {!!a.auteur  && <Text style={s.detail}><Text style={s.detailLbl}>{t('inventaire.auteur')} : </Text>{a.auteur}</Text>}
                {!!a.editeur && <Text style={s.detail}><Text style={s.detailLbl}>{t('inventaire.editeur')} : </Text>{a.editeur}</Text>}
                {!!a.annee   && <Text style={s.detail}><Text style={s.detailLbl}>{t('inventaire.parution')} : </Text>{a.annee}</Text>}
                {!!a.pages   && <Text style={s.detail}><Text style={s.detailLbl}>{t('inventaire.pages')} : </Text>{a.pages}</Text>}
              </>
            )}

            {/* Historique d'inventaire (compat MoPCal `histInv`) */}
            {histInv.length > 0 && (
              <>
                <Text style={s.sect}>{t('inventaire.histInvTitre')}</Text>
                {histInv.slice(0, 10).map((h, i) => (
                  <View key={i} style={[s.histo, i === 0 && s.histoActif]}>
                    <Text style={s.histoTxt}>{dateFr(h.date)} · {etatLabel(h.etat)}</Text>
                    {h.deplace && (
                      <Text style={s.histoSub}>🚚 {cheminCourt(h.empAvant)} → {cheminCourt(h.empApres)}</Text>
                    )}
                    {!!h.raison && <Text style={s.histoSub}>💬 {h.raison}</Text>}
                  </View>
                ))}
              </>
            )}

            {/* Achat */}
            {(prixFr(a.prix) || a.dateAchat) && (
              <>
                <Text style={s.sect}>{t('inventaire.achat')}</Text>
                <Text style={s.ligneFiche}>
                  {prixFr(a.prix) || '—'}{a.dateAchat ? `  ·  ${t('inventaire.dateAchat')} : ${dateFr(a.dateAchat)}` : ''}
                </Text>
              </>
            )}

            {/* Code-barres */}
            {!!a.codeBarres && (
              <>
                <Text style={s.sect}>{t('inventaire.codeBarres')}</Text>
                <Text style={s.mono}>{a.codeBarres}</Text>
              </>
            )}

            {/* Photos supplémentaires */}
            {photos.length > 1 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 14 }}>
                {photos.map((p, i) => (
                  <TouchableOpacity key={i} activeOpacity={0.85} onPress={() => setZoom(p.url)}>
                    <Image source={{ uri: p.url }} style={s.miniPhoto} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <View style={{ height: 30 }} />
          </ScrollView>

          {/* « Où est-ce rangé ? » — calque au-dessus de la fiche (pas de 2e Modal). */}
          <LocalisationModal
            visible={localiser && !!a.emplacementId}
            emplacementId={a.emplacementId}
            emplacements={emplacements}
            compteDe={compteDe}
            onFermer={() => setLocaliser(false)}
          />

          {/* Photo en plein écran (tap sur une vignette) — calque, pas de 2e Modal. */}
          <VisionneuseImage url={zoom} onFermer={() => setZoom(null)} />
    </Feuille>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '86%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  corps:   { padding: 16 },
  hIc:     { fontSize: 18, color: UI.accent },
  entete:      { flexDirection: 'row', gap: 14, marginBottom: 16 },
  grandePhoto: { width: 96, height: 130, borderRadius: 10, backgroundColor: UI.champ },
  photoVide:   { alignItems: 'center', justifyContent: 'center' },
  titre: { fontSize: 20, fontWeight: '800', color: UI.ink },
  sous:  { fontSize: 13.5, color: UI.muted, marginTop: 4 },
  colsRow:  { flexDirection: 'row', gap: 12, alignItems: 'flex-start', marginTop: 2, marginBottom: 4 },
  colTree:  { flex: 1, minWidth: 0 },
  colPlan:  { flex: 1, minWidth: 0, alignItems: 'center' },
  ligneFiche: { fontSize: 15, fontWeight: '600', color: UI.ink },
  ligneFaint: { fontSize: 12.5, color: UI.faint, marginTop: 2 },
  empLigne:  { fontSize: 14, color: UI.accent, fontWeight: '600', marginBottom: 3 },
  sect:  { fontSize: 12, fontWeight: '800', color: UI.accent, textTransform: 'uppercase',
           letterSpacing: 0.5, marginTop: 18, marginBottom: 8 },
  histo:      { borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, marginBottom: 6, backgroundColor: UI.card,
                borderWidth: 1, borderColor: UI.border },
  histoActif: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  histoTxt:   { fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  histoSub:   { fontSize: 12, color: UI.muted, marginTop: 3 },
  mono:   { fontSize: 15, color: UI.ink, letterSpacing: 1 },
  detail:    { fontSize: 14, color: UI.ink, marginBottom: 4 },
  detailLbl: { fontSize: 14, color: UI.muted, fontWeight: '700' },
  miniPhoto: { width: 90, height: 120, borderRadius: 10, backgroundColor: UI.champ, marginRight: 8 },
});
